import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ModalActions } from '../../components/PromptDialog';
import { useCanWrite } from '../auth/permissions';
import { publishingApi, type PublicationPreview, type PublicationRow, type Selection } from './publishingApi';
import { ResolvePublication } from './publishing/ResolvePublication';
import { AddPublication } from './publishing/AddPublication';
import { EditPublication } from './publishing/EditPublication';
import { Setting } from './publishing/Setting';
import { PublishingDialog } from './publishing/PublishingDialog';
import {
  colors,
  button,
  field,
  fixedStates,
  stateNames,
  channelLabel,
  today,
  monday,
  plusDays,
  dayLabel,
} from './publishing/publishingUi';

export function WochenplanungPage(): JSX.Element {
  const [params] = useSearchParams();
  const linkedPost = params.get('post');
  const linkedOnce = useRef<string | null>(null);
  const [resolving, setResolving] = useState<PublicationRow | null>(null);
  const [from, setFrom] = useState(() => monday(today()));
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [adding, setAdding] = useState<string | null>(null);
  const [editing, setEditing] = useState<PublicationRow | null>(null);
  const [preview, setPreview] = useState<{
    data: PublicationPreview;
    items: Selection[];
    batchId: string;
  } | null>(null);
  const [showConnections, setShowConnections] = useState(false);
  const canWrite = useCanWrite();
  const qc = useQueryClient();
  const week = useQuery({
    queryKey: ['social', 'publishing', from],
    queryFn: () => publishingApi.week(from),
    refetchInterval: 10000,
  });
  const location = useQuery({
    queryKey: ['social', 'publishing-location', linkedPost],
    queryFn: () => publishingApi.location(linkedPost!),
    enabled: Boolean(linkedPost),
  });
  const connections = useQuery({
    queryKey: ['social', 'connections'],
    queryFn: publishingApi.connections,
    enabled: showConnections,
    staleTime: 60000,
  });
  const run = async (action: () => Promise<unknown>, success = 'Gespeichert.'): Promise<boolean> => {
    if (busy) return false;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await action();
      setMessage(success);
      await qc.invalidateQueries({ queryKey: ['social'] });
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Änderung fehlgeschlagen.');
      return false;
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    if (location.data?.date && linkedOnce.current !== linkedPost) setFrom(monday(location.data.date));
  }, [location.data, linkedPost]);
  useEffect(() => {
    if (
      !linkedPost ||
      linkedOnce.current === linkedPost ||
      !location.data ||
      !week.data ||
      (location.data.date && from !== monday(location.data.date))
    )
      return;
    linkedOnce.current = linkedPost;
    const row = week.data.posts.find((p) => p.id === linkedPost);
    if (!row || !row.publishAt) {
      setMessage('Für diese Fassung fehlt noch ein vollständiger Termin. Bitte zuerst einplanen.');
      return;
    }
    if (!canWrite || fixedStates.includes(row.state)) return;
    const items = [{ postId: row.id, version: row.version }];
    setSelected([row.id]);
    void publishingApi
      .preview(items)
      .then((data) => setPreview({ data, items, batchId: crypto.randomUUID() }))
      .catch((e) => setError(e instanceof Error ? e.message : 'Vorschau fehlgeschlagen.'));
  }, [linkedPost, location.data, week.data, from, canWrite]);
  const changeWeek = (date: string) => {
    setFrom(monday(date));
    setSelected([]);
    setPreview(null);
  };
  const rows = week.data?.posts ?? [];
  const editable = rows.filter((row) => !fixedStates.includes(row.state));
  const items = rows
    .filter((row) => selected.includes(row.id) && !fixedStates.includes(row.state))
    .map((row) => ({ postId: row.id, version: row.version }));
  const global = week.data?.settings.find((s) => s.id === 'GLOBAL');
  const openPreview = () =>
    run(async () => {
      const data = await publishingApi.preview(items);
      setPreview({ data, items, batchId: crypto.randomUUID() });
    }, 'Vorschau erstellt.');
  const activate = async () => {
    if (!preview) return;
    let report = '';
    const ok = await run(async () => {
      const result = await publishingApi.activate(preview.items, preview.data.previewToken, preview.batchId);
      const failures = result.results.filter((r) => r.error);
      report = `${result.results.filter((r) => !r.error).length} Aufträge übernommen.${failures.length ? ' Offen: ' + failures.map((r) => `${rows.find((p) => p.id === r.postId)?.title ?? r.postId} (${channelLabel(rows.find((p) => p.id === r.postId)?.channel ?? '')}): ${r.error}`).join(' · ') : ''}`;
    });
    if (ok) {
      setMessage(report);
      setPreview(null);
      setSelected([]);
    }
  };
  return (
    <main className="mx-auto max-w-[1440px] p-4 lg:p-6" style={{ color: colors.textPrimary }}>
      <Link to="/social" className="text-sm" style={{ color: colors.accentLight }}>
        ‹ Kalender
      </Link>
      <header className="my-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Woche planen</h1>
          <p className="mt-1 text-sm" style={{ color: colors.textSecondary }}>
            Videos auswählen, Termine prüfen und automatisch veröffentlichen lassen.
          </p>
          <p className="mt-1 text-xs" style={{ color: colors.textTertiary }}>
            Europe/Berlin · Ohne rechtzeitige Video-Freigabe wird der Termin angehalten.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            className={button}
            onClick={() => changeWeek(plusDays(from, -7))}
            aria-label="Vorherige Woche"
          >
            ‹
          </button>
          <button className={button} onClick={() => changeWeek(today())}>
            Diese Woche
          </button>
          <button className={button} onClick={() => changeWeek(plusDays(from, 7))}>
            Nächste Woche ›
          </button>
          <label className="text-xs">
            Woche ab
            <input
              className={field}
              aria-label="Woche auswählen"
              type="date"
              value={from}
              onChange={(e) => {
                if (e.target.value) changeWeek(e.target.value);
              }}
            />
          </label>
        </div>
      </header>
      {week.isLoading && <p role="status">Wochenplan wird geladen …</p>}
      {week.error && <p role="alert">Wochenplan konnte nicht geladen werden: {week.error.message}</p>}
      {week.data?.mode === 'disabled' && (
        <p
          className="mb-3 rounded-lg p-3 text-sm"
          style={{ background: colors.warningBg, color: colors.warning }}
        >
          Versand ist noch nicht freigeschaltet. Du kannst die Woche vorbereiten; Aktivieren ist nach der
          Einrichtung möglich.
        </p>
      )}
      {week.data?.mode === 'fake' && (
        <p className="mb-3 rounded-lg p-3 text-sm" style={{ background: colors.infoBg, color: colors.info }}>
          Testbetrieb: Diese Umgebung veröffentlicht keine Inhalte.
        </p>
      )}
      {error && !adding && !editing && !preview && !resolving && (
        <p role="alert" className="mb-3 rounded border p-3" style={{ color: colors.danger }}>
          {error}
        </p>
      )}
      {message && (
        <p role="status" className="mb-3 text-sm">
          {message}
        </p>
      )}
      {week.data && (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-2">
            {canWrite && (
              <>
                <button
                  className={button}
                  disabled={busy || !editable.length}
                  onClick={() => setSelected(editable.map((p) => p.id))}
                >
                  Alle planbaren Termine auswählen
                </button>
                <button
                  className={button}
                  disabled={busy || !items.length}
                  onClick={() => void openPreview()}
                  style={{ background: colors.accent }}
                >
                  {items.length
                    ? `${items.length} ${items.length === 1 ? 'Termin' : 'Termine'} prüfen`
                    : 'Auswahl prüfen'}
                </button>
                {selected.length > 0 && (
                  <button className={button} onClick={() => setSelected([])}>
                    Auswahl aufheben
                  </button>
                )}
              </>
            )}
            {global && canWrite && (
              <button
                className={button}
                disabled={busy}
                onClick={() =>
                  void run(
                    () => publishingApi.setting(global, { paused: !global.paused }),
                    global.paused
                      ? 'Neue Übergaben wieder erlaubt.'
                      : 'Neue Übergaben pausiert. Laufende Aufträge werden weiter abgeglichen.',
                  )
                }
                style={{ color: global.paused ? colors.warning : colors.textPrimary }}
              >
                {global.paused ? 'Versand fortsetzen' : 'Versand pausieren'}
              </button>
            )}
            {global?.paused && (
              <span role="status" style={{ color: colors.warning }}>
                Versand pausiert
              </span>
            )}
            <button
              className={button}
              aria-expanded={showConnections}
              onClick={() => setShowConnections((v) => !v)}
            >
              Verbindungen & Einstellungen
            </button>
          </div>
          {showConnections && (
            <section
              className="mb-5 rounded-lg border border-white/15 p-4"
              aria-label="Verbindungen und Einstellungen"
            >
              <h2 className="mb-3 font-semibold">Verbindungen & Einstellungen</h2>
              {connections.isLoading && <p>Verbindungen werden geprüft …</p>}
              {connections.error && <p role="alert">Verbindungen konnten nicht geprüft werden.</p>}
              <div className="grid gap-3 md:grid-cols-3">
                {week.data.settings
                  .filter((s) => s.id !== 'GLOBAL')
                  .map((setting) => (
                    <Setting
                      key={setting.id + setting.version}
                      setting={setting}
                      canWrite={canWrite}
                      busy={busy}
                      account={connections.data?.accounts.find((a) => a.channel === setting.id)}
                      save={(patch) => void run(() => publishingApi.setting(setting, patch))}
                    />
                  ))}
              </div>
              {global && (
                <label className="mt-4 flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    disabled={!canWrite || busy}
                    checked={global.metaCrosspostingConfirmed}
                    onChange={(e) =>
                      void run(() =>
                        publishingApi.setting(global, { metaCrosspostingConfirmed: e.target.checked }),
                      )
                    }
                  />
                  <span>
                    Geprüft: Instagram veröffentlicht nicht zusätzlich automatisch auf Facebook. So entsteht
                    dort kein Doppelpost.
                  </span>
                </label>
              )}
            </section>
          )}
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            {Array.from({ length: 7 }, (_, n) => plusDays(from, n)).map((date) => {
              const posts = rows.filter((p) => p.date === date);
              const contributionCount = new Set(posts.map((p) => p.groupId ?? `${p.assetId}:${p.publishAt}`))
                .size;
              const paused = week.data.pauses.includes(date);
              return (
                <section
                  key={date}
                  className="min-w-0 rounded-xl border p-3"
                  style={{
                    background: colors.bgSurface,
                    borderColor: date === today() ? colors.accent : colors.borderSubtle,
                  }}
                  aria-label={dayLabel(date)}
                >
                  <h2 className="mb-1 font-semibold">{dayLabel(date)}</h2>
                  <p className="mb-3 text-xs" style={{ color: colors.textTertiary }}>
                    {contributionCount} {contributionCount === 1 ? 'Beitrag' : 'Beiträge'} · {posts.length}{' '}
                    {posts.length === 1 ? 'Veröffentlichung' : 'Veröffentlichungen'}
                  </p>
                  {paused ? (
                    <p className="my-5 text-sm" style={{ color: colors.textSecondary }}>
                      Bewusste Pause
                    </p>
                  ) : (
                    !posts.length && (
                      <p className="my-5 text-sm" style={{ color: colors.textTertiary }}>
                        Noch kein Beitrag eingeplant.
                      </p>
                    )
                  )}
                  <ul className="space-y-3">
                    {posts.map((row) => (
                      <li key={row.id} className="rounded-lg border border-white/10 p-3">
                        <div className="flex items-start gap-2">
                          {canWrite && !fixedStates.includes(row.state) && (
                            <input
                              className="mt-1"
                              aria-label={`${row.title} auf ${channelLabel(row.channel)} auswählen`}
                              type="checkbox"
                              checked={selected.includes(row.id)}
                              onChange={(e) =>
                                setSelected((s) =>
                                  e.target.checked ? [...s, row.id] : s.filter((id) => id !== row.id),
                                )
                              }
                            />
                          )}
                          <div className="min-w-0">
                            <p className="text-xs font-semibold" style={{ color: colors.accentLight }}>
                              {row.publishAt ? row.time : 'Uhrzeit fehlt'} · {channelLabel(row.channel)}
                            </p>
                            <Link
                              to={`/social/post/${row.id}`}
                              className="mt-1 block break-words text-sm font-semibold underline-offset-2 hover:underline"
                            >
                              {row.title}
                            </Link>
                          </div>
                        </div>
                        <p className="mt-2 text-xs">
                          {stateNames[row.state] ?? row.state} ·{' '}
                          {row.reviewWaiver
                            ? 'Review-Verzicht dokumentiert'
                            : row.ready
                              ? 'Video freigegeben'
                              : 'wartet auf Freigabe'}
                        </p>
                        {(row.reason || row.issue) && (
                          <p className="mt-2 text-xs" style={{ color: colors.warning }}>
                            {row.reason || row.issue}
                          </p>
                        )}
                        {row.removedAt && (
                          <p className="mt-1 text-xs">
                            Entfernt am{' '}
                            {new Date(row.removedAt).toLocaleDateString('de-DE', {
                              timeZone: 'Europe/Berlin',
                            })}
                          </p>
                        )}
                        {row.liveUrl && row.state !== 'REMOVED' && (
                          <a
                            href={row.liveUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-2 inline-block text-sm underline"
                          >
                            Veröffentlichung öffnen ↗
                          </a>
                        )}
                        {canWrite && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {!fixedStates.includes(row.state) ? (
                              <>
                                <button className={button} disabled={busy} onClick={() => setEditing(row)}>
                                  Termin ändern
                                </button>
                                {['ARMED', 'PREPARING'].includes(row.state) && (
                                  <button
                                    className={button}
                                    disabled={busy}
                                    onClick={() =>
                                      void run(() => publishingApi.cancel(row), 'Automatik abgebrochen.')
                                    }
                                  >
                                    Abbrechen
                                  </button>
                                )}
                                <button
                                  className={button}
                                  disabled={busy}
                                  onClick={() =>
                                    void run(
                                      () => publishingApi.unschedule(row),
                                      'Termin entfernt; Video bleibt im Bestand.',
                                    )
                                  }
                                >
                                  Aus Woche nehmen
                                </button>
                              </>
                            ) : (
                              row.attemptId &&
                              !['LIVE', 'SIMULATED', 'REMOVED'].includes(row.state) && (
                                <button
                                  className={button}
                                  disabled={busy}
                                  onClick={() =>
                                    void run(
                                      () => publishingApi.reconcile(row.attemptId!),
                                      'Statusabgleich angefordert.',
                                    )
                                  }
                                >
                                  Status abgleichen
                                </button>
                              )
                            )}
                            {row.attemptId && ['LIVE', 'UNKNOWN'].includes(row.state) && (
                              <button
                                className={button}
                                disabled={busy}
                                onClick={() => {
                                  setError('');
                                  setResolving(row);
                                }}
                              >
                                {row.state === 'LIVE' ? 'Zurücknehmen' : 'Veröffentlichungslink bestätigen'}
                              </button>
                            )}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                  {canWrite && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {!paused && (
                        <button className={button} disabled={busy} onClick={() => setAdding(date)}>
                          + Beitrag einplanen
                        </button>
                      )}
                      {!posts.length && (
                        <button
                          className={button}
                          disabled={busy}
                          onClick={() =>
                            void run(
                              () => publishingApi.pause(date, !paused),
                              paused ? 'Pause aufgehoben.' : 'Tag bleibt bewusst frei.',
                            )
                          }
                        >
                          {paused ? 'Pause aufheben' : 'Tag frei lassen'}
                        </button>
                      )}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        </>
      )}
      {resolving && (
        <ResolvePublication
          row={resolving}
          error={error}
          busy={busy}
          close={() => {
            if (!busy) setResolving(null);
          }}
          save={async (input) => {
            if (
              await run(
                () =>
                  resolving.state === 'LIVE'
                    ? publishingApi.removal(resolving, input.reason)
                    : publishingApi.confirmLive(resolving, input),
                resolving.state === 'LIVE' ? 'Entfernung dokumentiert.' : 'Veröffentlichungslink bestätigt.',
              )
            )
              setResolving(null);
          }}
        />
      )}
      {adding && week.data && (
        <AddPublication
          date={adding}
          error={error}
          candidates={week.data.candidates}
          settings={week.data.settings}
          busy={busy}
          close={() => {
            if (!busy) setAdding(null);
          }}
          save={async (input) => {
            if (
              await run(
                () => publishingApi.clone(input),
                'Beitrag eingeplant. Anschließend prüfen und aktivieren.',
              )
            )
              setAdding(null);
          }}
        />
      )}
      {editing && (
        <EditPublication
          error={error}
          row={editing}
          busy={busy}
          close={() => {
            if (!busy) setEditing(null);
          }}
          save={async (input) => {
            if (
              await run(
                () => publishingApi.schedule(editing, input),
                'Termin gespeichert; Automatik bitte neu aktivieren.',
              )
            )
              setEditing(null);
          }}
        />
      )}
      {preview && (
        <PublishingDialog
          title="Veröffentlichungen prüfen"
          close={() => {
            if (!busy) setPreview(null);
          }}
          submit={() => void activate()}
        >
          {error && (
            <p role="alert" className="mb-3 text-sm" style={{ color: colors.danger }}>
              {error}
            </p>
          )}
          <p className="mb-3 text-sm">
            Diese Auswahl wird zu den angegebenen Zeiten veröffentlicht, sobald das Video rechtzeitig
            freigegeben ist. Fehlende Freigaben halten den Termin an.
          </p>
          <div className="max-h-[55vh] space-y-3 overflow-y-auto">
            {preview.data.rows.map((row) => (
              <article className="rounded border border-white/15 p-3 text-sm" key={row.postId}>
                {row.error ? (
                  <p role="alert" style={{ color: colors.warning }}>
                    {row.error}
                  </p>
                ) : (
                  <>
                    <h3 className="font-semibold">
                      {row.title} · {channelLabel(row.channel ?? '')}
                    </h3>
                    <p>
                      {row.publishAt &&
                        new Date(row.publishAt).toLocaleString('de-DE', { timeZone: 'Europe/Berlin' })}{' '}
                      · Berlin
                    </p>
                    <p className="my-2 whitespace-pre-wrap break-words">{row.caption}</p>
                    <p className="mb-2 text-xs">
                      KI-Kennzeichnung: {row.aiLabel ? 'Ja' : 'Nein'} · Werbung:{' '}
                      {row.advertising ? 'Ja' : 'Nein'}
                    </p>
                    {row.hasCover && (
                      <p className="mb-2 text-xs" style={{ color: colors.warning }}>
                        Das hochgeladene Cover wird beim automatischen Versand noch nicht übernommen. Der
                        Kanal verwendet ein Bild aus dem Video.
                      </p>
                    )}
                    {row.tags?.length ? <p className="mb-2 text-xs">Tags: {row.tags.join(', ')}</p> : null}
                    {row.options && (
                      <p className="mb-2 text-xs">
                        {row.channel === 'YOUTUBE'
                          ? `Öffentlich · Speziell für Kinder: ${row.options.madeForKids ? 'Ja' : 'Nein'}`
                          : row.channel === 'INSTAGRAM'
                            ? `Reel · Im Feed teilen: ${row.options.shareToFeed ? 'Ja' : 'Nein'}`
                            : row.options.facebookMediaType === 'REELS'
                              ? 'Facebook-Reel'
                              : 'Facebook-Seitenvideo'}
                      </p>
                    )}
                    <p className="text-xs">
                      {row.reviewWaiver
                        ? 'Review-Verzicht dokumentiert'
                        : row.ready
                          ? 'Video freigegeben'
                          : 'Wartet auf Christines Freigabe'}
                    </p>
                    {row.reviewComment && <p className="mt-1 text-xs">Anmerkung: {row.reviewComment}</p>}
                    <p className="mt-1 break-all text-xs">
                      Ziel: {row.target?.profile} · {row.target?.targetId}
                    </p>
                  </>
                )}
              </article>
            ))}
          </div>
          {preview.data.rows.some((r) => r.error) && (
            <p className="mt-2 text-xs">
              Nur fehlerfreie Termine können übernommen werden. Offene Termine bleiben unverändert.
            </p>
          )}
          <ModalActions
            onCancel={() => {
              if (!busy) setPreview(null);
            }}
            confirmLabel={
              preview.data.mode === 'fake' ? 'Test-Aufträge aktivieren' : 'Automatisch veröffentlichen'
            }
            disabled={busy || preview.data.mode === 'disabled' || preview.data.rows.every((r) => r.error)}
          />
        </PublishingDialog>
      )}
    </main>
  );
}
